import { useEffect } from 'react';
import { findNeighbor, type NavDirection, type NavPosition, type TreeNavGraph } from './treeNavigation';

// ─── Tastatur-Navigation im Stammbaum ───────────────────────────────────────
//
// Hört global mit, hält sich aber aus Eingabefeldern und Dialogen heraus –
// dort gehören die Tasten der Eingabe bzw. dem Dialog.

const ARROW_TO_DIRECTION: Record<string, NavDirection> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

/** Tippt der Nutzer gerade irgendwo? Dann keine Baum-Navigation. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    el.isContentEditable === true
  );
}

interface TreeKeyboardNavOptions {
  /** Nur aktiv, wenn der Baum sichtbar ist. */
  enabled: boolean;
  graph: TreeNavGraph;
  positions: Map<string, NavPosition>;
  selectedPersonId: string | null;
  /** Person auswählen und Ansicht zu ihr fahren. */
  onNavigate: (personId: string) => void;
  /** Detail-Panel für die aktuelle Auswahl öffnen. */
  onOpenDetails: (personId: string) => void;
  onDeselect: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCenter: () => void;
  onFocusSearch: () => void;
}

export function useTreeKeyboardNav({
  enabled,
  graph,
  positions,
  selectedPersonId,
  onNavigate,
  onOpenDetails,
  onDeselect,
  onZoomIn,
  onZoomOut,
  onCenter,
  onFocusSearch,
}: TreeKeyboardNavOptions): void {
  useEffect(() => {
    if (!enabled) return;

    function handleKeyDown(e: KeyboardEvent) {
      // Browser-/System-Kürzel (Cmd+K, Ctrl+F …) nicht abfangen.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      // Offener Dialog (Modal, Schnellsuche) hat Vorrang.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;

      const direction = ARROW_TO_DIRECTION[e.key];
      if (direction) {
        // Ohne Auswahl an einem sinnvollen Punkt einsteigen: oben links.
        if (!selectedPersonId) {
          let entry: string | null = null;
          let bestScore = Infinity;
          for (const [id, pos] of positions) {
            const score = pos.y * 1000 + pos.x;
            if (score < bestScore) {
              bestScore = score;
              entry = id;
            }
          }
          if (entry) {
            e.preventDefault();
            onNavigate(entry);
          }
          return;
        }
        const next = findNeighbor(selectedPersonId, direction, graph, positions);
        if (next) {
          e.preventDefault();
          onNavigate(next);
        }
        return;
      }

      if (e.key === 'Enter') {
        if (selectedPersonId) {
          e.preventDefault();
          onOpenDetails(selectedPersonId);
        }
      } else if (e.key === 'Escape') {
        if (selectedPersonId) {
          e.preventDefault();
          onDeselect();
        }
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        onZoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        onZoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        onCenter();
      } else if (e.key === '/') {
        e.preventDefault();
        onFocusSearch();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    enabled,
    graph,
    positions,
    selectedPersonId,
    onNavigate,
    onOpenDetails,
    onDeselect,
    onZoomIn,
    onZoomOut,
    onCenter,
    onFocusSearch,
  ]);
}
